import React from "react";
import moment from "moment-timezone";
import {Tooltip} from "@material-ui/core";
import {Button, ButtonGroup, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import Framework from "@beardeddevops/react.framework";

const layawayModeProperty = {name: "report_layaway_mode", label: "Layaway payments"};
const workOrderModeProperty = {name: "report_work_order_mode", label: "Work Order payments"};
const timezoneModeProperty = {name: "report_timezone_mode", label: "Report timezone"};

export const INVOICE_STATUS_OPTIONS = [
	{value: null, label: "All"},
	{value: 0, label: "Unpaid Invoices"},
	{value: 1, label: "Paid Invoices"},
	{value: 2, label: "Written Off Invoices"},
];

function ButtonSwitchWithTooltip({
	value,
	property,
	update,
	labelTrue,
	labelFalse,
	title,
}) {
	return (
		<Tooltip title={title}>
			<span className="report-toolbar-switch-wrap">
				<div className="form-group report-toolbar-switch" style={{marginBottom: 0}}>
					<Framework.Elements.ButtonSwitch
						value={!!value}
						property={property}
						update={update}
						labelTrue={labelTrue}
						labelFalse={labelFalse}
						project={'pos'}
					/>
				</div>
			</span>
		</Tooltip>
	);
}

/**
 * Toolbar row: layaway + WO switches (left), date range picker (center), timezone switch (right).
 * Left/right columns share equal flex so the date block stays visually centered.
 * Includes all date-range modals so report collections stay thin.
 */
export default function ReportDateToolbar({
	filterDate,
	start_date,
	end_date,
	dateRangeFormatted,
	showDate,
	showStartDate,
	showEndDate,
	showDateRange,
	onToggleModal,
	onSingleDateChange,
	onStartDateChange,
	onEndDateChange,
	onApplyDateRange,
	onCancelDateRange,
	toggleClose,
	completedLayawaysOnly,
	completedWorkOrdersOnly,
	useLocalTimezone,
	showLayawayToggle = true,
	showWorkOrderToggle = true,
	onLayawayModeChange,
	onWorkOrderModeChange,
	onTimezoneModeChange,
	invoiceStatusFilter,
	showInvoiceStatusToggle = false,
	onInvoiceStatusChange,
}) {
	const localTzName = moment.tz.guess();
	const displayLabel =
		dateRangeFormatted || moment(filterDate).format("dddd MMM D");

	const handleStartDateUpdate = (name, date) => {
		if (moment(date).isAfter(moment(end_date))) {
			alert("Start date may not be after the end date.");
			onToggleModal("showStartDate");
			return;
		}
		onStartDateChange(moment(date).format("YYYY-MM-DD"));
		onToggleModal("showStartDate");
	};

	const handleEndDateUpdate = (name, date) => {
		if (moment(date).isBefore(moment(start_date))) {
			alert("End date may not be before the start date.");
			onToggleModal("showEndDate");
			return;
		}
		onEndDateChange(moment(date).format("YYYY-MM-DD"));
		onToggleModal("showEndDate");
	};

	return (
		<>
			<style>{`
				.report-date-toolbar {
					display: grid;
					grid-template-columns: auto 1fr auto;
					align-items: center;
					margin-bottom: 12px;
					column-gap: 12px;
					row-gap: 8px;
				}
				.report-date-toolbar .report-date-toolbar-left {
					display: flex;
					flex-direction: row;
					flex-wrap: nowrap;
					align-items: center;
					gap: 8px;
				}
				.report-date-toolbar .report-date-toolbar-left .report-toolbar-switch-wrap {
					display: flex;
					flex: 0 0 auto;
					flex-direction: row;
				}
				.report-date-toolbar .report-date-toolbar-left .report-toolbar-switch {
					display: flex;
					flex: 0 0 auto;
					flex-direction: row;
					width: auto;
					max-width: none;
					margin-bottom: 0;
				}
				.report-date-toolbar .report-date-toolbar-left .report-toolbar-switch .btn-group,
				.report-date-toolbar .report-date-toolbar-left .report-toolbar-switch .btn-group-toggle {
					width: auto;
					max-width: none;
				}
				.report-date-toolbar .report-invoice-status-group {
					display: flex;
					flex: 0 0 auto;
					flex-wrap: nowrap;
				}
				.report-date-toolbar .report-invoice-status-group .btn {
					white-space: nowrap;
					font-size: 0.8125rem;
					padding: 0.25rem 0.5rem;
				}
				.report-date-toolbar .report-date-toolbar-right {
					display: flex;
					align-items: center;
					justify-content: flex-end;
				}
				.report-date-toolbar .report-date-toolbar-center {
					display: flex;
					flex: 1 1 auto;
					justify-content: center;
					align-items: center;
					flex-wrap: nowrap;
					gap: 8px;
					min-width: 0;
				}
				.report-date-toolbar .report-date-range-btn {
					white-space: nowrap;
					flex-shrink: 0;
				}
				.report-date-toolbar .report-date-toolbar-label {
					white-space: nowrap;
					flex-shrink: 1;
					min-width: 0;
					overflow: hidden;
					text-overflow: ellipsis;
				}
				@media (max-width: 768px) {
					.report-date-toolbar {
						grid-template-columns: 1fr;
					}
					.report-date-toolbar .report-date-toolbar-left {
						justify-content: flex-start;
					}
					.report-date-toolbar .report-date-toolbar-center {
						justify-content: center;
					}
					.report-date-toolbar .report-date-toolbar-right {
						justify-content: flex-end;
					}
				}
			`}</style>
			<Modal
				isOpen={showDate}
				toggle={() => onToggleModal("showDate")}
				size={"sm"}
				backdrop={true}
				style={{display: "none"}}
			>
				<ModalBody>
					<Framework.Elements.Date
						label={"Custom Date"}
						value={filterDate}
						update={onSingleDateChange}
						open={showDate}
						clearable={false}
						cancelLabel={""}
					/>
				</ModalBody>
			</Modal>
			<Modal
				isOpen={showDateRange}
				toggle={() => onToggleModal("showDateRange")}
				size={"lg"}
				backdrop={true}
			>
				<ModalHeader>Select Start and End Date</ModalHeader>
				<ModalBody>
					<div className={"row"}>
						<div className={"col-6"}>
							<div>
								Start Date &nbsp;&nbsp;
								<Button
									style={{whiteSpace: "nowrap"}}
									onClick={() => onToggleModal("showStartDate")}
								>
									{moment(start_date).format("dddd MMM D")}
								</Button>
							</div>
						</div>
						<div className={"col-6"}>
							<div>
								End Date &nbsp;&nbsp;
								<Button
									style={{whiteSpace: "nowrap"}}
									onClick={() => onToggleModal("showEndDate")}
								>
									{moment(end_date).format("dddd MMM D")}
								</Button>
							</div>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						className={"primary-button"}
						onClick={() => {
							onToggleModal("showDateRange");
							onApplyDateRange();
						}}
					>
						OK
					</Button>
					<Button className={"secondary-button"} onClick={onCancelDateRange}>
						Cancel
					</Button>
				</ModalFooter>
			</Modal>
			<Modal
				isOpen={showStartDate}
				toggle={toggleClose}
				size={"sm"}
				backdrop={true}
				style={{display: "none"}}
			>
				<ModalBody>
					<Framework.Elements.Date
						label={"Custom Date"}
						value={start_date}
						update={handleStartDateUpdate}
						open={showStartDate}
						clearable={false}
						cancelLabel={""}
					/>
				</ModalBody>
			</Modal>
			<Modal
				isOpen={showEndDate}
				toggle={toggleClose}
				size={"sm"}
				backdrop={true}
				style={{display: "none"}}
			>
				<ModalBody>
					<Framework.Elements.Date
						label={"Custom Date"}
						value={end_date}
						update={handleEndDateUpdate}
						open={showEndDate}
						clearable={false}
						cancelLabel={""}
					/>
				</ModalBody>
			</Modal>

			<div className="report-date-toolbar">
				<div className="report-date-toolbar-left">
					{showLayawayToggle ? (
						<ButtonSwitchWithTooltip
							value={!!completedLayawaysOnly}
							property={layawayModeProperty}
							update={(_property, nextValue) => onLayawayModeChange(!!nextValue)}
							labelTrue="Completed Layaways"
							labelFalse="Partial Layaways"
							title="Partial Layaways includes all layaway payments in the date range, including incomplete layaways. Completed Layaways includes only payments on layaways that are fully paid off."
						/>
					) : null}
					{showWorkOrderToggle ? (
						<ButtonSwitchWithTooltip
							value={!!completedWorkOrdersOnly}
							property={workOrderModeProperty}
							update={(_property, nextValue) => onWorkOrderModeChange(!!nextValue)}
							labelTrue="Completed WOs"
							labelFalse="Partial WOs"
							title="Partial Work Orders includes all work order payments in the date range, including incomplete work orders. Completed Work Orders includes only payments on work orders that are fully paid off."
						/>
					) : null}
					{showInvoiceStatusToggle ? (
						<Tooltip title="Filter invoice sales and payments by the invoice's current status. All includes every invoice-related row in the date range.">
							<span className="report-toolbar-switch-wrap">
								<ButtonGroup className="report-invoice-status-group">
									{INVOICE_STATUS_OPTIONS.map((option) => (
										<Button
											key={option.label}
											className={invoiceStatusFilter === option.value ? 'primary-button-buttongroup' : 'outline-button-primary'}
											outline={invoiceStatusFilter !== option.value}
											onClick={() => onInvoiceStatusChange(option.value)}
										>
											{option.label}
										</Button>
									))}
								</ButtonGroup>
							</span>
						</Tooltip>
					) : null}
				</div>
				<div className="report-date-toolbar-center">
					<Button
						className="report-date-range-btn"
						onClick={() => onToggleModal("showDateRange")}
					>
						Select Date Range
					</Button>
					<div className="report-date-toolbar-label">
						<label
							style={{cursor: "pointer", marginBottom: 0}}
							onClick={() => onToggleModal("showDate")}
						>
							{displayLabel}
						</label>
					</div>
				</div>
				<div className="report-date-toolbar-right">
					<ButtonSwitchWithTooltip
						value={!!useLocalTimezone}
						property={timezoneModeProperty}
						update={(_property, nextValue) => onTimezoneModeChange(!!nextValue)}
						labelTrue="Local TZ"
						labelFalse="Server TZ"
						title={`Server TZ filters report dates using the server clock. Local TZ filters using your browser timezone (${localTzName}).`}
						project={'pos'}
					/>
				</div>
			</div>
		</>
	);
}

export function formatDateRangeLabel(startDate, endDate) {
	return (
		moment(startDate).format("dddd MMM D") + " - " + moment(endDate).format("dddd MMM D")
	);
}

export function createDefaultReportDateState(localStorageKey) {
	let filterDate = moment().format();
	try {
		if (localStorageKey && localStorage.getItem(localStorageKey)) {
			filterDate = moment(localStorage.getItem(localStorageKey)).format();
		}
	} catch (e) {
		// ignore localStorage errors
	}
	const end_date = moment().format("YYYY-MM-DD");
	const start_date = moment().subtract(30, "days").format("YYYY-MM-DD");
	return {
		filterDate,
		showDate: false,
		showStartDate: false,
		showEndDate: false,
		showDateRange: false,
		start_date,
		end_date,
		dateRangeFormatted: formatDateRangeLabel(start_date, end_date),
	};
}

export function formatReportMoney(value) {
	const n = parseFloat(value);
	return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

export function buildReportApiFilter(state) {
	const filter = {
		day: state.filterDate,
		start_date: state.start_date,
		end_date: state.end_date,
		completed_layaways_only: state.completedLayawaysOnly === true,
		completed_work_orders_only: state.completedWorkOrdersOnly === true,
		client_timezone: state.useLocalTimezone ? moment.tz.guess() : null,
	};
	if (Object.prototype.hasOwnProperty.call(state, "invoiceStatusFilter")
		&& state.invoiceStatusFilter != null) {
		filter.invoice_status = state.invoiceStatusFilter;
	}
	return filter;
}

/**
 * Standard report filter defaults for NEW reports (see SalesByCategoryReport.js as the template).
 *
 * Do not retrofit existing reports — only use this full toolbar pattern when adding new report Collections.
 *
 * Required toolbar controls (all enabled by default on ReportDateToolbar):
 * - completedLayawaysOnly  → partial vs completed layaway payments only
 * - completedWorkOrdersOnly → partial vs completed work order payments only
 * - useLocalTimezone       → Local TZ (browser) vs Server TZ (default true)
 * - Date range picker      → filterDate, start_date, end_date + modal state from createDefaultReportDateState
 *
 * Setup checklist for a new report Collection:
 * 1. const DATE_STORAGE_KEY = "dateYourReport";  // unique per report
 * 2. Object.assign(this.state, defaultReportFilterState, createDefaultReportDateState(DATE_STORAGE_KEY));
 * 3. model.filter = buildReportApiFilter(this.state) in getReport()
 * 4. Render <ReportDateToolbar ... /> with all handlers (copy from SalesByCategoryReport.js):
 *    handleLayawayModeChange, handleWorkOrderModeChange, handleTimezoneModeChange,
 *    handleToggleModal, handleDateChange, handleCancelDateRange, onApplyDateRange={getReport}
 * 5. Do NOT pass showLayawayToggle={false} or showWorkOrderToggle={false} unless the report
 *    genuinely excludes those payment types from its data source.
 *
 * Backend: Locations_Sales.buildReportFilterContext() / filterReportSourceRows() already honor
 * completed_layaways_only, completed_work_orders_only, and client_timezone from buildReportApiFilter.
 *
 * Reference implementations: SalesByCategoryReport.js (new reports), SalesReport.js, TaxableBreakdownReports.js
 */
export const defaultReportFilterState = {
	completedLayawaysOnly: false,
	completedWorkOrdersOnly: false,
	useLocalTimezone: true,
};

export const defaultInvoiceReportFilterState = {
	...defaultReportFilterState,
	invoiceStatusFilter: null,
};
